import React from "react";
import { Box, Flex, Stack, Spinner, Badge, Text, useColorModeValue } from "@chakra-ui/react";
import dayjs from "dayjs";
import LegalStatusBadge from "./LegalStatusBadge";

/**
 * Renders a single version entry with status badge and metadata.
 * @param {object} props
 * @param {object} props.version - Version data with status, date, etc.
 * @param {boolean} props.isCurrent - Whether this is the current active version
 * @returns {React.ReactElement}
 */
function VersionEntry({ version, isCurrent }) {
    const verColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    const dateColor = useColorModeValue("secondaryGray.500", "secondaryGray.400");
    const entryBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const currentBg = useColorModeValue("brand.50", "rgba(118,81,255,0.12)");
    return (
        <Box p="12px" borderRadius="10px" bg={isCurrent ? currentBg : entryBg}
            border="1px solid" borderColor={isCurrent ? "brand.400" : "transparent"}>
            <Flex justify="space-between" align="center">
                <Stack direction="row" spacing="8px" align="center">
                    <Text fontSize="0.8rem" fontWeight="600" color={verColor}>v{version.version}</Text>
                    <LegalStatusBadge status={version.status} />
                    {isCurrent && (
                        <Badge bg="brand.100" color="brand.500" borderRadius="full" px="6px"
                            fontSize="0.6rem" fontWeight="600" textTransform="none">Current</Badge>
                    )}
                </Stack>
                <Text fontSize="0.7rem" color={dateColor}>{dayjs(version.created_at).format("DD/MM/YYYY HH:mm")}</Text>
            </Flex>
        </Box>
    );
}

/**
 * Displays version history timeline for a legal document.
 * Shows loading spinner, empty state, or list of version entries.
 * @param {object} props
 * @param {Array} props.versions - Array of document versions sorted by date desc
 * @param {boolean} props.loading - Loading state
 * @param {number} props.currentId - Current document ID for highlighting
 * @returns {React.ReactElement}
 */
function VersionHistory({ versions, loading, currentId }) {
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (loading) return <Box textAlign="center" py="24px"><Spinner size="sm" color="brand.500" /></Box>;
    if (!versions || versions.length <= 1) {
        return <Text color={emptyColor} fontSize="0.78rem" textAlign="center" py="16px">No previous versions</Text>;
    }
    return (
        <Stack spacing="8px">
            {versions.map((v) => <VersionEntry key={v.id} version={v} isCurrent={v.id === currentId} />)}
        </Stack>
    );
}

export default VersionHistory;
